import type { ReactNode } from "react";

/**
 * The frame around sign-in and registration.
 *
 * It is the one place in the product with a full-bleed composition: a dark panel
 * carrying the proposition beside the form on paper. Everything past this point is
 * dense working interface, so this is where the product gets to introduce itself.
 */
export function AuthFrame({
  title,
  subtitle,
  children,
  footer,
  wide = false,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Proposition panel */}
      <div className="relative flex flex-col justify-between overflow-hidden bg-ink px-7 py-10 text-paper lg:w-[42%] lg:px-12 lg:py-14">
        {/* Ruled lines receding into the dark: a ledger page seen edge-on. */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, transparent, transparent 31px, currentColor 31px, currentColor 32px)",
          }}
        />
        <div
          className="pointer-events-none absolute -right-28 -top-28 h-72 w-72 rounded-full opacity-25 blur-3xl"
          aria-hidden="true"
          style={{ background: "radial-gradient(circle, var(--color-verdigris-bright), transparent 70%)" }}
        />

        <div className="relative">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-[26px] leading-none">PayFlow</span>
            <span className="h-1.5 w-1.5 rounded-full bg-brass" aria-hidden="true" />
          </div>
          <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-paper/40">
            Payments · Bushveld
          </p>
        </div>

        <div className="relative my-12 lg:my-0">
          <h2 className="font-display text-[34px] leading-[1.12] lg:text-[42px]">
            Take payments across Zimbabwe
            <span className="italic text-brass"> from one integration.</span>
          </h2>
          <p className="mt-5 max-w-md text-[14px] leading-relaxed text-paper/60">
            EcoCash, InnBucks, OneMoney, O'mari, SmileCash, eWallet and cards — settled into your
            bank account, with fees you can see on every transaction.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              "Sandbox keys the moment you register",
              "One API for every payment rail",
              "Signed webhooks and a full audit trail",
            ].map((item, i) => (
              <li
                key={item}
                className={`settle settle-${i + 1} flex items-start gap-2.5 text-[13.5px] text-paper/75`}
              >
                <span className="mt-[7px] h-px w-4 shrink-0 bg-brass" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center px-5 py-12 sm:px-8">
        <div className={`settle w-full ${wide ? "max-w-lg" : "max-w-sm"}`}>
          <h1 className="text-[28px] leading-tight text-ink-strong">{title}</h1>
          <p className="mb-7 mt-1.5 text-[13.5px] leading-relaxed text-ink-muted">{subtitle}</p>
          {children}
          {footer && <p className="mt-6 text-[13px] text-ink-muted">{footer}</p>}
        </div>
      </div>
    </div>
  );
}
